import PropTypes from 'prop-types';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import MessageType from '../../../../enums/MessageType';
import CheckIcon from '../../../../imgs/CheckIcon';
import LoyaltyPointsIcon from '../../../../imgs/LoyaltyPointsIcon';
import Message from '../../../../v2/components/ui/Message/Message';
import Translation from '../../../../v2/lang/Translation';
import {
  getGuestCheckoutAccountBenefits,
  isNewGuestUpdatedPage,
} from '../../../../v2/selectors/selectors';
import isUsingArtemisStyles from '../../../../v2/selectors/isUsingArtemisStyles';
import useStyles from './AccountBenefits.styles';

const benefitHeaders = {
  myprotein: 'want.more.join.us',
  dermstore: 'join.best.in.beauty',
};

const benefitsDefinitions = {
  referral: [
    'account.benefits.exclusive.range',
    'account.benefits.refer',
  ],
  rewardsAndReferral: [
    'account.benefits.rewards.points',
    'account.benefits.refer',
  ],
  rewardsPoints: [
    'account.benefits.rewards.points',
    'account.benefits.early.access',
  ],
  default: [
    'account.benefits.exclusive.range',
    'account.benefits.early.access',
    'account.benefits.beauty.offers',
    'account.benefits.create.wishlist',
  ],
  lfint: [
    'account.benefits.track.account.order',
    'account.benefits.referral.credit',
    'account.benefits.beauty.offers',
    'account.benefits.create.wishlist',
  ],
  myprotein: [
    'account.benefits.track.account.order',
    'account.benefits.referral.credit',
    'account.benefits.myp.offers',
    'account.benefits.create.wishlist',
  ],
  dermstore: [
    'account.benefits.receive.emails',
    'account.benefits.easier',
    'account.benefits.manage.wishlist',
    'account.benefits.track.orders',
  ],
  lux: [
    'account.benefits.latest.arrivals',
    'account.benefits.early.access',
  ],
  loyalty: [
    'participate.in.loyalty',
    'earn.points.on.purchases',
    'earn.points.for.review.or.referral',
  ],
};

export function AccountBenefits(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  return (
    <>
      {props.newGuestUpdatedPage && (
      <h2 className={classes.header} data-test="AccountBenefits__header">
        <Translation optionalExtension={props.siteCode}>want.more.join.us</Translation>
      </h2>
      )}
      
      {!props.newGuestUpdatedPage && (
        <>
          <h2><Translation>account.header</Translation></h2>
        
          <p className={`AccountBenefits__email ${classes.email}`} data-test="AccountBenefits__email" data-cs-mask>
            <Translation placeholderValues={[props.email]}>account.benefits</Translation>
          </p>
        </>
      )}
      
      {props.rewardPointsApplied && (
        <div className={classes.pointsMessage} data-test="AccountBenefits__rewardsPointsMessage">
          <Message type={MessageType.INFO}>
            <Translation placeholderValues={[props.rewardPoints.collected]}>
              youve.earned.x.points
            </Translation>
          </Message>
        </div>
      )}
      
      <ul className={classes.list}>
        {props.showLFLoyalty && (
          <li className={classes.loyaltyListItem} data-test="AccountBenefits__loyaltyList">
            <LoyaltyPointsIcon className={classes.loyaltyIcon} />
            <Translation>account.benefits.lf.rewards</Translation>
          </li>
        )}
        {(!props.guestCheckoutAccountBenefitsList) && (
          benefitsDefinitions[props.benefitsList].map(benefitListItem => (
            <li className={classes.listItem} data-test="AccountBenefits__list">
              {props.newGuestUpdatedPage && <CheckIcon className={classes.listIcon} />}
              <Translation optionalExtension={props.siteCode}>{benefitListItem}</Translation>
            </li>
          ))
        )}
        {(props.guestCheckoutAccountBenefitsList) && (
          props.guestCheckoutAccountBenefitsList.map(benefitListItem => (
            <li className={classes.listItem} data-test="AccountBenefits__list">
              {props.newGuestUpdatedPage && <CheckIcon className={classes.listIcon} />}
              <Translation optionalExtension={props.siteCode}>{benefitListItem}</Translation>
            </li>
          ))
        )}
      </ul>
      
      {props.newGuestUpdatedPage && (
        <p className={`AccountBenefits__email ${classes.email}`} data-test="AccountBenefits__email" data-cs-mask>
          <Translation placeholderValues={[props.email]}>account.benefits</Translation>
        </p>
      )}
    </>
  );
}

AccountBenefits.propTypes = {
  email: PropTypes.string.isRequired,
  rewardPointsApplied: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rewardPoints: PropTypes.object,
  benefitsList: PropTypes.string.isRequired,
  siteCode: PropTypes.string.isRequired,
  newGuestUpdatedPage: PropTypes.bool.isRequired,
  showLFLoyalty: PropTypes.bool.isRequired,
  isUsingArtemisStyles: PropTypes.bool,
  guestCheckoutAccountBenefitsList: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = (state) => {
  const {clientConfiguration} = state.start;
  const {siteCode} = state.start.siteDataV2;
  const isLuxSite = !!state.start.settings.lux_benefits_list_enabled;
  const hasRewardPoints = clientConfiguration?.hasRewardPoints;
  const hasLoyaltyPoints = state.start.settings.loyalty_banner;
  const hasReferralScheme = clientConfiguration?.hasReferralScheme;
  const hasRewardsAndReferrals = hasRewardPoints && hasReferralScheme;
  const newGuestUpdatedPage = isNewGuestUpdatedPage(state);
  const guestCheckoutAccountBenefitsList = getGuestCheckoutAccountBenefits(state);
  
  let guestCheckoutBenefitList = '';
  
  if (newGuestUpdatedPage) {
    guestCheckoutBenefitList = benefitsDefinitions[siteCode] ? siteCode : undefined;
  }
  
  return {
    newGuestUpdatedPage,
    guestCheckoutAccountBenefitsList,
    siteCode,
    isNewGuestUpdatedPage,
    getGuestCheckoutAccountBenefits,
    isUsingArtemisStyles: isUsingArtemisStyles(state),
    benefitHeader: isNewGuestUpdatedPage && benefitHeaders[siteCode],
    showLFLoyalty: isNewGuestUpdatedPage && siteCode === 'lfint' && hasLoyaltyPoints,
    rewardPoints: state.start.order.rewardPoints || {},
    rewardPointsApplied: !!state.start.order.rewardPoints && state.start.order.rewardPoints.spent > 0,
    email: state.start.customer.email,
    benefitsList: guestCheckoutBenefitList
      || (hasLoyaltyPoints && 'loyalty')
      || (isLuxSite && 'lux')
      || (hasRewardsAndReferrals && 'rewardsAndReferral')
      || (hasRewardPoints && 'rewardsPoints')
      || (hasReferralScheme && 'referral')
      || 'default',
  };
};

export default connect(mapStateToProps)(AccountBenefits);
